import { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronsUp, ChevronsDown, Sun, Zap, Wind, Battery, ServerCrash, Activity, Wifi, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { GroupedUnitList } from './GroupedUnitList';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import type { Unit } from '@/types';

export interface SidebarRef {
  scrollToUnit: (unitId: string) => void;
}

interface SidebarProps {
  units: Unit[];
  totalUnits?: number; // Total count before viewport filtering
  selectedUnit: Unit | null;
  hoveredUnit?: Unit | null;
  onSelectUnit: (unit: Unit | null, tab?: 'solar' | 'charger' | 'hvac' | 'starlink') => void;
  onHoverUnit: (unit: Unit | null) => void;
  isCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  error?: string | null;
  onResetViewportFilter?: () => void; // Callback to disable viewport filtering
}

type SortField = 'name' | 'battery' | 'solar' | 'charger' | 'hvac' | 'status' | 'starlink' | null;
type SortDirection = 'asc' | 'desc';

export const Sidebar = forwardRef<SidebarRef, SidebarProps>(({
  units,
  totalUnits,
  selectedUnit,
  hoveredUnit,
  onSelectUnit,
  onHoverUnit,
  isCollapsed = false,
  onCollapsedChange,
  error,
  onResetViewportFilter,
}, ref) => {
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const collapsed = onCollapsedChange ? isCollapsed : internalIsCollapsed;
  const setCollapsed = onCollapsedChange ? (val: boolean) => onCollapsedChange(val) : setInternalIsCollapsed;
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(false);
  const [, setHighlightedUnit] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hoverLeaveTimeout = useRef<NodeJS.Timeout | null>(null);
  const { permissions } = useUserPermissions();

// Cleanup timeouts on unmount to prevent state updates after unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      if (hoverLeaveTimeout.current) {
        clearTimeout(hoverLeaveTimeout.current);
      }
    };
  }, []);

  // Expose scrollToUnit method to parent component
  useImperativeHandle(ref, () => ({
    scrollToUnit: (unitId: string) => {
      const item = listRef.current?.querySelector(`[data-unit-id="${unitId}"]`);
      if (item) {
        item.scrollIntoView({ behavior: 'instant', block: 'center' });
        setHighlightedUnit(unitId);

        // Clear existing timeout before creating new one
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }

        // Auto-clear highlight after 2 seconds
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedUnit(null);
          highlightTimeoutRef.current = null;
        }, 2000);
      }
    }
  }));

  // Sort units based on selected field
  // Note: units prop is already filtered by parent (Dashboard)
  const sortedUnits = [...units].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: number;
    let bValue: number;

    switch (sortField) {
      case 'name': {
        const nameCompare = a.unit_code.localeCompare(b.unit_code);
        return sortDirection === 'asc' ? nameCompare : -nameCompare;
      }
      case 'status': {
        // Define priority order: online -> offline -> idle -> unknown
        const statusPriority: Record<string, number> = {
          'online': 1,
          'offline': 2,
          'idle': 3,
          'unknown': 4
        };

        const aPriority = statusPriority[a.status] || 99;
        const bPriority = statusPriority[b.status] || 99;

        return sortDirection === 'asc'
          ? aPriority - bPriority
          : bPriority - aPriority;
      }
      case 'battery':
        aValue = a.systems?.solar?.battery ?? 0;
        bValue = b.systems?.solar?.battery ?? 0;
        break;
      case 'solar':
        aValue = (a.systems?.solar?.generation ?? 0) > 0 ? 1 : 0;
        bValue = (b.systems?.solar?.generation ?? 0) > 0 ? 1 : 0;
        break;
      case 'charger':
        aValue = a.systems?.charger?.enabled ? 1 : 0;
        bValue = b.systems?.charger?.enabled ? 1 : 0;
        break;
      case 'hvac':
        aValue = a.systems?.hvac?.enabled ? 1 : 0;
        bValue = b.systems?.hvac?.enabled ? 1 : 0;
        break;
      case 'starlink':
        // Sort by Starlink connection status
        aValue = a.systems?.starlink?.connectionStatus === 'online' ? 1 : 0;
        bValue = b.systems?.starlink?.connectionStatus === 'online' ? 1 : 0;
        break;
      default:
        return 0;
    }

    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <motion.aside
      className="relative bg-card flex flex-col border-r border-border h-full shadow-lg"
      initial={false}
      animate={{ width: collapsed ? 100 : 360 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{ willChange: 'width' }}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-1/2 -translate-y-1/2 z-50 w-6 h-6 bg-border hover:bg-accent rounded-full flex items-center justify-center transition-colors shadow-md"
      >
        {collapsed ? (
          <ChevronRight className="h-3 w-3" />
        ) : (
          <ChevronLeft className="h-3 w-3" />
        )}
      </button>

      {/* Sort Headers */}
      <AnimatePresence mode="wait">
        {!collapsed && (
          <motion.div
            className="px-4 py-2 border-b border-border bg-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {/* Status Sort Button */}
              <button
                onClick={() => handleSort('status')}
                className="w-6 flex items-center justify-center hover:text-foreground transition-colors relative -ml-1"
                title="Sort by Status"
              >
                <Activity className="h-3 w-3" />
                {sortField === 'status' && (
                  <ChevronDown className={cn(
                    "h-2 w-2 absolute translate-y-2",
                    sortDirection === 'asc' && "rotate-180"
                  )} />
                )}
              </button>

              {/* UNITS label with count - clickable for name sorting */}
              <button
                onClick={() => handleSort('name')}
                className="flex-1 min-w-0 text-left hover:text-foreground transition-colors"
                title="Sort by Model"
              >
                {totalUnits && sortedUnits.length < totalUnits ? (
                  <>
                    <span className="text-[#8FB560] font-semibold">{sortedUnits.length}</span>
                    <span>/{totalUnits}</span>
                    <span> UNITS</span>
                  </>
                ) : (
                  <span>{sortedUnits.length} UNITS</span>
                )}
                {sortField === 'name' && (
                  <ChevronDown className={cn(
                    "h-2 w-2 inline-block ml-1",
                    sortDirection === 'asc' && "rotate-180"
                  )} />
                )}
              </button>

              {/* Expand/Collapse All Groups Toggle - Centered */}
              <button
                onClick={() => setAllGroupsExpanded(prev => !prev)}
                className="flex items-center gap-0.5 text-[#8FB560] hover:text-[#7fa050] transition-colors mx-auto"
                title={allGroupsExpanded ? "Collapse all groups" : "Expand all groups"}
              >
                {allGroupsExpanded ? (
                  <ChevronsUp className="h-3 w-3" />
                ) : (
                  <ChevronsDown className="h-3 w-3" />
                )}
                <span className="text-[8px] font-semibold uppercase tracking-tight leading-none">ALL</span>
              </button>

              {/* Battery + System Icons Group - shifted 8px left */}
              <div className="sidebar-header-icons-group flex items-center gap-2 flex-shrink-0">
                {/* Battery sort - centered over battery column (percentage + bar) */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div className="min-w-[24px]"></div>
                  <button
                    onClick={() => handleSort('battery')}
                    className="w-3 flex items-center justify-center hover:text-foreground transition-colors"
                    title="Sort by Battery"
                  >
                    <Battery className="h-3 w-3" />
                    {sortField === 'battery' && (
                      <ChevronDown className="h-2 w-2 absolute translate-y-2" />
                    )}
                  </button>
                </div>

                {/* System Icons sort - centered over each icon */}
                <div className="flex gap-3 items-center flex-shrink-0">
                  <button
                    onClick={() => handleSort('solar')}
                    className="w-4 flex items-center justify-center hover:text-foreground transition-colors relative"
                    title="Sort by Solar"
                  >
                    <Sun className="h-3 w-3" />
                    {sortField === 'solar' && (
                      <ChevronDown className="h-2 w-2 absolute translate-y-2" />
                    )}
                  </button>
                  <button
                    onClick={() => handleSort('charger')}
                    className="w-4 flex items-center justify-center hover:text-foreground transition-colors relative"
                    title="Sort by Charger"
                  >
                    <Zap className="h-3 w-3" />
                    {sortField === 'charger' && (
                      <ChevronDown className="h-2 w-2 absolute translate-y-2" />
                    )}
                  </button>
                  <button
                    onClick={() => handleSort('hvac')}
                    className="w-4 flex items-center justify-center hover:text-foreground transition-colors relative"
                    title="Sort by HVAC"
                  >
                    <Wind className="h-3 w-3" />
                    {sortField === 'hvac' && (
                      <ChevronDown className="h-2 w-2 absolute translate-y-2" />
                    )}
                  </button>
                  <button
                    onClick={() => handleSort('starlink')}
                    className="w-4 flex items-center justify-center hover:text-foreground transition-colors relative"
                    title="Sort by Starlink"
                  >
                    <Wifi className="h-3 w-3" />
                    {sortField === 'starlink' && (
                      <ChevronDown className="h-2 w-2 absolute translate-y-2" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unit List */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {error && sortedUnits.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <ServerCrash className="h-16 w-16 opacity-50" />
                {!collapsed && (
                  <p className="text-sm text-center px-4 opacity-70">Failed to load units</p>
                )}
              </div>
            </div>
          ) : collapsed ? (
            <div className="flex flex-col gap-0.5 p-1" onMouseLeave={() => onHoverUnit(null)}>
              {sortedUnits.map((unit) => (
                <button
                  key={unit.id}
                  onClick={() => onSelectUnit(unit)}
                  onMouseEnter={() => onHoverUnit(unit)}
                  className={cn(
                    'flex items-center gap-1 px-1 py-1 rounded hover:bg-accent transition-colors w-full min-w-0',
                    selectedUnit?.id === unit.id && 'bg-accent',
                    hoveredUnit?.id === unit.id && selectedUnit?.id !== unit.id && 'bg-[#8FB560]/10 dark:bg-[#8FB560]/15'
                  )}
                  title={unit.unit_code}
                >
                  <div className={cn(
                    "w-2 h-2 rounded-full flex-shrink-0",
                    unit.status === 'online' && "bg-success",
                    unit.status === 'offline' && "bg-error",
                    unit.status === 'idle' && "bg-gray-400",
                    unit.status === 'unknown' && "bg-gray-300"
                  )} />
                  <span className="text-[10px] font-medium truncate font-mono">{unit.unit_code}</span>
                </button>
              ))}
            </div>
          ) : (
            <div ref={listRef} className="flex flex-col h-full">
              <GroupedUnitList
                units={sortedUnits}
                permissions={permissions}
                selectedUnit={selectedUnit}
                hoveredUnit={hoveredUnit}
                onUnitClick={(unit) => onSelectUnit(unit)}
                onUnitHover={onHoverUnit}
                onCloseDetails={() => onSelectUnit(null)}
                sortBy={sortField || 'name'}
                sortOrder={sortDirection}
                allExpanded={allGroupsExpanded}
                onToggleAll={() => setAllGroupsExpanded(prev => !prev)}
              />

              {/* Reset Filtered View Button - appears when viewport filter is active */}
              {totalUnits && sortedUnits.length < totalUnits && onResetViewportFilter && (
                <div className="p-4 border-t border-border bg-card/50 flex-shrink-0">
                  <button
                    onClick={onResetViewportFilter}
                    className="w-full flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium text-[#8FB560] hover:text-[#8FB560] hover:bg-[#8FB560]/10 border border-[#8FB560]/30 rounded transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset View and Zoom
                  </button>
                </div>
              )}
            </div>
          )}
        </AnimatePresence>
      </div>
    </motion.aside>
  );
});

Sidebar.displayName = 'Sidebar';
